/**
 *
 *  /EmailPreferences/Organizations
 *
 */

import React, { useEffect, useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ToastContainer } from 'react-toastify'
import { useHistory, useLocation } from 'react-router-dom'
import { queryVariables } from '@joeyparis/utils'

// Graphql
import useQuery from 'components/UseQuery'
import { useMutation, gql } from '@apollo/client'

// Components
import DropDownMenu from 'components/DropDownMenu'
import EmailPreferences from '..'
import Spinner from 'components/Spinner'

// Utils
import { get_timeline } from '../email-timelines'
import { category_options, options_list, TypeSelectorContainer, timelines_download_links } from '../Users'

const GET_TEMPLATES = gql`
	query organization_contact_email_templates($organization_id: ID!, $category: String, $topic: String) {
		organization_contact_email_templates(organization_id: $organization_id, category: $category, topic: $topic) {
			id
			name
			template_index
			email_template_name
			global_default
			has_html
			can_edit
			default(organization_id: $organization_id)
			topic
		}
		organization(id: $organization_id) {
			communication_preference: communication_preference_by_type(category: $category, topic: $topic) {
				id
				email_template_defaults
				contact_emails
				contact_sms
			}
		}
	}
`

const REMOVE_EMAIL_TEMPLATE = gql`
	mutation deleteEmailTemplate($id: ID!) {
		deleteEmailTemplate(input: { id: $id }) {
			email_template {
				id
			}
		}
	}
`

const COPY_EMAIL_TEMPLATE = gql`
	mutation copyEmailTemplate($id: ID!, $organization_id: ID, $topic: String) {
		copyEmailTemplate(input: { id: $id, organization_id: $organization_id, topic: $topic }) {
			email_template {
				id
			}
		}
	}
`

function LoadingView() {
	return (
		<div className="d-flex align-items-center" style={{ height: '700px' }}>
			<Spinner />
		</div>
	)
}

function OrganizationEmailPreferences({ organization_id, category }) {
	const history = useHistory()
	const location = useLocation()
	const { topic, template_index } = queryVariables(location)

	const [selected_topic, setSelectedTopic] = useState('taxes_in_retirement_567')
	const [default_template, setDefaultTemplate] = useState('')

	const redirect_hash = 'organization'

	const {
		data: { organization, organization_contact_email_templates },
		loading,
		refetch,
	} = useQuery(GET_TEMPLATES, {
		variables: {
			organization_id,
			category: category === 'appointment' ? 'appointment' : 'event',
			topic: category === 'event_567' ? selected_topic : null,
		},
		channels: [organization_id],
		default_data: {
			organization: {},
		},
		skip: !organization_id,
	})

	useEffect(() => {
		refetch()
	}, [selected_topic])

	useEffect(() => {
		if (topic && options_list.some((opt) => opt.value === topic)) {
			setSelectedTopic(topic)
		}
	}, [topic])

	useEffect(() => {
		if (template_index !== null && template_index !== undefined) {
			setDefaultTemplate(template_index)
		}
	}, [template_index])

	const [copyEmailTemplate] = useMutation(COPY_EMAIL_TEMPLATE, {
		onCompleted: ({ copyEmailTemplate: { email_template } }) => {
			refetch()
			history.push(`/email_templates/${email_template?.id}/edit#organization`)
		},
	})

	const [removeEmailTemplate] = useMutation(REMOVE_EMAIL_TEMPLATE, { onCompleted: refetch })

	const handleCopyTemplate = (variables) => {
		copyEmailTemplate({
			variables: {
				organization_id,
				...variables,
				topic: category === 'event_567' ? selected_topic : null,
			},
		})
	}

	const handleRemoveTemplate = (template_id) => {
		removeEmailTemplate({
			variables: {
				id: template_id,
			},
		})
	}

	const handleChangeTopic = (new_selected_topic) => {
		const url = new URL(window.location)
		const params = new URLSearchParams(url.search)

		params.delete('template_index')
		params.delete('topic')

		url.search = params.toString()
		window.history.pushState({}, '', url)

		setDefaultTemplate('')
		setSelectedTopic(new_selected_topic)
	}

	return (
		<div>
			<ToastContainer />

			<div className="d-flex">
				<TypeSelectorContainer>
					<div className="d-flex">
						<h4 className="title">{category_options[category]} Timeline</h4>

						{category === 'event_567' && (
							<div className="sub-title">
								<span className="">Topic: </span>
								<DropDownMenu
									Button={
										<div className="type-selector">
											<span>
												{options_list.find((opt) => opt.value === selected_topic)?.text}
											</span>

											<FontAwesomeIcon icon="caret-down" className="ml-2" />
										</div>
									}
									button_style={{ textDecoration: 'none' }}
								>
									<ul className="list-unstyled" style={{}}>
										{options_list.map((option) => (
											<li
												key={option.value}
												onClick={() => handleChangeTopic(option.value)}
												role="presentation"
											>
												{option.text}
											</li>
										))}
									</ul>
								</DropDownMenu>
							</div>
						)}
					</div>

					{!(category === 'appointment') && (
						<a href={timelines_download_links[category]} target="_blank" className="btn btn-purple">
							<FontAwesomeIcon icon="download" className="mr-2" />
							Download Timeline
						</a>
					)}
				</TypeSelectorContainer>
			</div>

			{organization && organization.communication_preference && (
				<EmailPreferences
					email_timeline={get_timeline(category)}
					email_templates={organization_contact_email_templates}
					communication_preference={organization.communication_preference}
					copyEmailTemplate={handleCopyTemplate}
					deleteEmailTemplate={handleRemoveTemplate}
					origin={redirect_hash}
					category={category}
					default_template_index={default_template}
				/>
			)}

			{loading && <LoadingView />}
		</div>
	)
}

OrganizationEmailPreferences.propTypes = {
	organization_id: PropTypes.string,
	category: PropTypes.string,
}

export default OrganizationEmailPreferences
